﻿import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePermission } from '../hooks/usePermissions'
import { useUserLogs } from '../hooks/useUserLogs'
import { Heading, Text, Button, Input, LoadingSpinner } from '../components/ui'
import { BRAND_COLOR } from '../constants'
import { 
  listItems,
  createItem,
  updateItem,
  deleteItem
} from '../services/informativo'
import { InformativoItem, CreateInformativoPayload } from '../types/informativo'
import { modulesService } from '../services/modules'
import { Module } from '../types'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

const LOG_ORIGEM = 'informativo/boletim'
const LOG_ENTITY_TYPE = 'informativo_boletim' as const

function boletimLogFields(row: {
  id?: string
  date: string
  title: string
  description: string
  link: string | null
  type?: string
}) {
  return {
    id: row.id ?? null,
    date: row.date,
    title: row.title,
    description: row.description,
    link: row.link,
    type: row.type ?? 'boletim',
  }
}

const Boletim: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'versoes' | 'gerenciar'>('versoes')
  
  // Hooks de permissões (após informativo_08: name = informativo_boletim)
  const { hasPermission: canShow } = usePermission('informativo_boletim', 'show')
  const { hasPermission: canInsert } = usePermission('informativo_boletim', 'insert')
  const { hasPermission: canUpdate } = usePermission('informativo_boletim', 'update')
  const { hasPermission: canDelete } = usePermission('informativo_boletim', 'delete')
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()
  
  // Estados principais
  const [items, setItems] = useState<InformativoItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [module, setModule] = useState<Module | null>(null)

  // Estados de modais (para aba Gerenciar)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<InformativoItem | null>(null)

  // Modal de visualização (como Interno)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<InformativoItem | null>(null)

  // Estados de formulário (para aba Gerenciar)
  const [formData, setFormData] = useState<CreateInformativoPayload>({
    date: '',
    title: '',
    description: '',
    file: null,
    link: null,
    type: 'boletim'
  })
  const [modalLoading, setModalLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // Estados de filtros e busca (para aba Versões)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  const [selectedDate, setSelectedDate] = useState(searchParams.get('data') || '')

  // Estados de paginação (para aba Versões)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)

  // Função para buscar dados
  const fetchData = async (opts?: { showOverlay?: boolean }) => {
    try {
      const showOverlay = opts?.showOverlay ?? true
      if (showOverlay) {
        setLoading(true)
      }
      setError(null)

      const itemsData = await listItems('boletim')
      setItems(itemsData)
      setRetryCount(0)
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
      setRetryCount(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  // Função de retry
  const retryFetch = () => {
    if (retryCount < 3) {
      fetchData()
    }
  }

  // Debounce do termo de busca
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // Resetar página para 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, selectedDate])

  // Carregar dados iniciais e aplicar filtro da URL
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      const dataParam = searchParams.get('data')
      if (dataParam) {
        setSelectedDate(dataParam)
      }
      fetchData({ showOverlay: true })
    }
  }, [searchParams])

  // Carregar informações do módulo
  useEffect(() => {
    const loadModule = async () => {
      const { data } = await modulesService.getByName('informativo_boletim')
      if (data?.id) setModule(data)
    }
    loadModule()
  }, [])

  // Funções de modal (para aba Gerenciar)
  const openModal = (item?: InformativoItem) => {
    // Verificar permissões
    if (item) {
      if (!canUpdate) {
        alert('Você não tem permissão para editar itens.')
        return
      }
    } else {
      if (!canInsert) {
        alert('Você não tem permissão para criar itens.')
        return
      }
    }

    if (item) {
      setEditingItem(item)
      setFormData({
        date: item.date,
        title: item.title,
        description: item.description,
        file: null,
        link: item.link,
        type: item.type
      })
    } else {
      setEditingItem(null)
      setFormData({
        date: '',
        title: '',
        description: '',
        file: null,
        link: null,
        type: 'boletim'
      })
    }
    setValidationErrors({})
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingItem(null)
    setValidationErrors({})
    setFormData({
      date: '',
      title: '',
      description: '',
      file: null,
      link: null,
      type: 'boletim'
    })
  }

  // Função de validação
  const validateForm = () => {
    const errors: Record<string, string> = {}
    
    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Título é obrigatório'
    }
    
    if (!formData.date || formData.date === '') {
      errors.date = 'Data é obrigatória'
    }
    
    if (!formData.description || formData.description.trim() === '') {
      errors.description = 'Descrição é obrigatória'
    }

    if (!formData.link || formData.link.trim() === '') {
      errors.link = 'Link é obrigatório'
    }
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const formatDescription = (description: string): string => {
    if (!description) return ''
    if (description.includes('<')) return description
    return description.replace(/\n/g, '<br />')
  }

  const handleCardClick = (item: InformativoItem) => {
    setSelectedItem(item)
    setShowViewModal(true)
    void logView(LOG_ENTITY_TYPE, item.id, `Visualizou boletim: ${item.title}`)
  }

  // Função para normalizar URL (adicionar protocolo se necessário)
  const normalizeUrl = (url: string): string => {
    if (!url) return url
    // Se já tem protocolo, retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Se não tem protocolo, adicionar https://
    return `https://${url}`
  }

  // Funções de CRUD
  const handleSave = async () => {
    if (!validateForm()) {
      return
    }
    
    try {
      setModalLoading(true)
      const payload: CreateInformativoPayload = {
        ...formData,
        file: null,
        link: formData.link?.trim() || null,
      }

      if (editingItem) {
        const oldValues = boletimLogFields(editingItem)
        await updateItem(editingItem.id, payload)
        await logUpdate(
          LOG_ENTITY_TYPE,
          editingItem.id,
          `Atualizou boletim: ${payload.title.trim()}`,
          oldValues,
          boletimLogFields({
            id: editingItem.id,
            date: payload.date,
            title: payload.title.trim(),
            description: payload.description,
            link: payload.link,
            type: payload.type,
          }),
          { origem: LOG_ORIGEM }
        )
      } else {
        const id = await createItem(payload)
        await logCreate(
          LOG_ENTITY_TYPE,
          id,
          `Criou boletim: ${payload.title.trim()}`,
          {
            origem: LOG_ORIGEM,
            new_values: boletimLogFields({
              id,
              date: payload.date,
              title: payload.title.trim(),
              description: payload.description,
              link: payload.link,
              type: payload.type,
            }),
          }
        )
      }
      
      await fetchData()
      closeModal()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao salvar item')
    } finally {
      setModalLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir itens.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir este item?')) {
      try {
        const item = items.find(i => i.id === id)
        await deleteItem(id)
        if (item) {
          await logDelete(
            LOG_ENTITY_TYPE,
            id,
            `Excluiu boletim: ${item.title}`,
            {
              origem: LOG_ORIGEM,
              old_values: boletimLogFields(item),
            }
          )
        }
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir item:', err)
        alert(err instanceof Error ? err.message : 'Erro ao excluir item')
      }
    }
  }

  // Limpar filtros (para aba Versões)
  const handleClearFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedDate('')
    setCurrentPage(1)
  }

  // Filtrar itens (para aba Versões)
  const filteredItems = items.filter(item => {
    const matchesSearch = !debouncedSearchTerm || 
      item.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    
    const matchesDate = !selectedDate || item.date === selectedDate
    
    return matchesSearch && matchesDate
  })

  // Calcular valores de paginação
  const totalItems = filteredItems.length
  const pagesCount = Math.ceil(totalItems / itemsPerPage)
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  
  const showingStart = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const showingEnd = Math.min(currentPage * itemsPerPage, totalItems)

  const hasFilters = debouncedSearchTerm || selectedDate

  // Ajustar página atual se ela for maior que o número total de páginas
  useEffect(() => {
    if (pagesCount > 0 && currentPage > pagesCount) {
      setCurrentPage(1)
    }
  }, [pagesCount, currentPage])

  // Funções de paginação
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagesCount) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <Text size="2xl" className="text-gray-600 dark:text-gray-400 mb-2">
                Carregando Boletim...
              </Text>
              {retryCount > 0 && (
                <Text size="sm" className="text-gray-500 dark:text-gray-500">
                  Tentativa {retryCount} de 3...
                </Text>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <Text size="2xl" className="text-red-600 dark:text-red-400 mb-2">
                Erro ao carregar Boletim
              </Text>
              <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </Text>
              <Button
                onClick={retryFetch}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tentar Novamente
              </Button>
              {retryCount > 0 && (
                <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                  Já tentamos {retryCount} vezes
                </Text>
              )}
            </div>
          </div>
        ) : !canShow ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Text size="2xl" className="text-gray-500 dark:text-gray-400 mb-2">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar o Boletim.
              </Text>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    {module?.display_name || 'Boletim'}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-2">
                    {activeTab === 'versoes' 
                      ? `${totalItems} ${totalItems === 1 ? 'boletim' : 'boletins'}`
                      : 'Gerencie os boletins de atualização do TI'
                    }
                  </Text>
                </div>
                {activeTab === 'gerenciar' && (
                  <Button
                    onClick={() => openModal()}
                    className="flex items-center gap-2"
                    style={{ backgroundColor: BRAND_COLOR }}
                  >
                    <PlusIcon className="h-5 w-5" />
                    Novo Item
                  </Button>
                )}
              </div>
            </div>

            {/* Abas */}
            <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('versoes')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'versoes'
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Versões
                </button>
                <button
                  onClick={() => setActiveTab('gerenciar')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'gerenciar'
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Gerenciar
                </button>
              </div>
            </div>

            {/* Conteúdo das Abas */}
            {activeTab === 'versoes' ? (
              <>
                {/* Filtros */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Text size="sm" className="mb-2">Buscar</Text>
                      <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Buscar por título ou descrição..."
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Text size="sm" className="mb-2">Data</Text>
                      <Input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </div>

                    <div className="flex items-end">
                      {hasFilters && (
                        <Button
                          onClick={handleClearFilters}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          <XCircleIcon className="h-4 w-4" />
                          Limpar filtros
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Lista de Artigos */}
                <div className="flex-1 overflow-y-auto p-6">
                  {paginatedItems.length === 0 ? (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <Text size="lg" className="text-gray-500 mb-2">
                        Nenhum boletim encontrado
                      </Text>
                      <Text size="sm" className="text-gray-400">
                        {hasFilters ? 'Tente ajustar os filtros' : 'Não há boletins cadastrados'}
                      </Text>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {paginatedItems.map((item) => (
                        <div 
                          key={item.id} 
                          onClick={() => handleCardClick(item)}
                          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-shadow cursor-pointer hover:shadow-md"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                                  {item.title}
                                </Heading>
                              </div>
                              
                              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="h-4 w-4" />
                                  {new Date(item.date).toLocaleDateString('pt-BR')}
                                </div>
                              </div>
                              
                              <div
                                className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 line-clamp-3 [&>*]:my-0 [&>*]:leading-tight"
                                dangerouslySetInnerHTML={{ __html: formatDescription(item.description) }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Paginação */}
                {!loading && totalItems > 0 && (
                  <div className="mt-6 w-full px-6 pb-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex items-center gap-4">
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          Mostrando {showingStart} a {showingEnd} de {totalItems} resultados
                        </Text>
                        <select
                          value={itemsPerPage.toString()}
                          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          por página
                        </Text>
                      </div>
                      
                      {pagesCount > 1 && (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            size="sm"
                            variant="outline"
                          >
                            Anterior
                          </Button>
                          
                          <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, pagesCount) }, (_, i) => {
                              const page = i + 1
                              return (
                                <Button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  size="sm"
                                  variant={currentPage === page ? "solid" : "outline"}
                                  className={currentPage === page ? "bg-primary-600 text-white" : ""}
                                >
                                  {page}
                                </Button>
                              )
                            })}
                            {pagesCount > 5 && (
                              <>
                                <Text size="sm" className="text-gray-500">...</Text>
                                <Button
                                  onClick={() => handlePageChange(pagesCount)}
                                  size="sm"
                                  variant={currentPage === pagesCount ? "solid" : "outline"}
                                  className={currentPage === pagesCount ? "bg-primary-600 text-white" : ""}
                                >
                                  {pagesCount}
                                </Button>
                              </>
                            )}
                          </div>
                          
                          <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagesCount}
                            size="sm"
                            variant="outline"
                          >
                            Próxima
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Lista de Itens - Gerenciar */}
                <div className="flex-1 overflow-y-auto p-6">
                  {items.length === 0 ? (
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <Text size="lg" className="text-gray-500 mb-2">
                        Nenhum item encontrado
                      </Text>
                      <Text size="sm" className="text-gray-400">
                        Clique em "Novo Item" para criar o primeiro
                      </Text>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Título
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Data
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Link
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {item.title}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {new Date(item.date).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {item.link ? (
                                  <a
                                    href={normalizeUrl(item.link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-700 flex items-center gap-1"
                                  >
                                    <DocumentTextIcon className="h-4 w-4" />
                                    Link
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <div title="Editar">
                                    <Button
                                      onClick={() => openModal(item)}
                                      size="sm"
                                      variant="outline"
                                      className="flex items-center justify-center"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div title="Excluir">
                                    <Button
                                      onClick={() => handleDelete(item.id)}
                                      size="sm"
                                      variant="outline"
                                      className="flex items-center justify-center text-red-600 hover:text-red-700"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modal de Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingItem ? 'Editar Item' : 'Novo Item'}
              </Heading>
              <Button
                onClick={closeModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Título */}
                <div>
                  <Text size="sm" className="mb-2">
                    Título <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Digite o título..."
                    className={validationErrors.title ? 'border-red-500' : ''}
                  />
                  {validationErrors.title && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.title}
                    </Text>
                  )}
                </div>

                {/* Data */}
                <div>
                  <Text size="sm" className="mb-2">
                    Data <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className={validationErrors.date ? 'border-red-500' : ''}
                  />
                  {validationErrors.date && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.date}
                    </Text>
                  )}
                </div>

                {/* Link */}
                <div>
                  <Text size="sm" className="mb-2">
                    Link <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    type="url"
                    value={formData.link || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value || null }))}
                    placeholder="https://exemplo.com/noticia"
                    className={validationErrors.link ? 'border-red-500' : ''}
                  />
                  {validationErrors.link && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.link}
                    </Text>
                  )}
                </div>

                {/* Descrição */}
                <div>
                  <Text size="sm" className="mb-2">
                    Descrição <span className="text-red-500">*</span>
                  </Text>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Digite a descrição..."
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white ${
                      validationErrors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    rows={5}
                  />
                  {validationErrors.description && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.description}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={modalLoading || !formData.title.trim() || !formData.description.trim() || !formData.date || !formData.link?.trim()}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingItem ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização (igual Interno) */}
      {showViewModal && selectedItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowViewModal(false)
            setSelectedItem(null)
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 bg-white dark:bg-gray-800">
              <div className="flex-1 flex items-center gap-4 flex-wrap">
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  {selectedItem.title}
                </Heading>
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <CalendarIcon className="h-4 w-4" />
                  {new Date(selectedItem.date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedItem(null)
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div
                    className="prose dark:prose-invert max-w-none text-sm"
                    dangerouslySetInnerHTML={{ __html: formatDescription(selectedItem.description) }}
                  />
                </div>
              </div>

              {selectedItem.link && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Link
                  </label>
                  <Button
                    onClick={() => {
                      const normalizedLink = normalizeUrl(selectedItem.link!)
                      window.open(normalizedLink, '_blank')
                    }}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Abrir Link
                  </Button>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedItem(null)
                  }}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Boletim





